import { Link } from 'react-router-dom';
import { Container } from '@/components/layout';
import { SacredGeometry, Sparkle } from '@/components/ornaments';
import { Reveal } from '@/components/motion';
import { Brandmark, ScriptLine } from '@/components/typography';
import { formatDateRangeHero, type TourEvent } from '@/features/events';
import { AssetImage } from '@/pages/shared/AssetImage';
import { HeroReservaCta } from '@/pages/shared/ConversionBlocks';
import { VslInline } from '@/pages/shared/VslInline';
import { BA_HERO } from '../ba.content';
import { MigaGira } from '@/pages/shared/MigaGira';
import styles from './BaHero.module.css';

interface Props {
  event: TourEvent;
}

/**
 * Hero de Buenos Aires. Composicion CENTRADA (a diferencia del hub): Obelisco a
 * la izquierda, retrato a la derecha, y toda la tipografia en el eje.
 *
 * DESVIACION B-1 (registrada): el mockup no trae cabecera, pero el gate exige
 * poder volver al hub desde cada ciudad. El bloque de marca centrado ES ese
 * enlace — mismo aspecto que el mockup, cero peso visual anadido.
 * (`Page` monta ademas la cabecera y el footer del sitio.)
 */
/**
 * Parte el titular como lo compone el mockup: dos palabras grandes separadas por
 * un conector pequeno entre filetes ("DESPROGRAMACIÓN / de la / PERCEPCIÓN").
 *
 * El acento script ("y Salto Cuántico") YA viene aparte en `scriptAccent`, asi
 * que aqui solo se separa por el conector. Si el titulo de un destino no lo
 * lleva, se devuelve entero y se pinta en un bloque: **nunca se corta a ciegas**.
 */
function partirTitular(tituloPrincipal: string) {
  const conectores = [' de la ', ' de los ', ' de las ', ' del ', ' de '];
  for (const conector of conectores) {
    const corte = tituloPrincipal.indexOf(conector);
    if (corte > 0) {
      return {
        head: tituloPrincipal.slice(0, corte),
        connector: conector.trim(),
        tail: tituloPrincipal.slice(corte + conector.length),
      };
    }
  }
  return { head: tituloPrincipal, connector: null, tail: null };
}

export function BaHero({ event }: Props) {
  // `titleMain` ya viene SIN el acento script, y `titleAccent` lo trae aparte:
  // no hay que trocear el titulo completo a mano.
  const { head, connector, tail } = partirTitular(event.titleMain);

  return (
    <section className={styles.hero} aria-labelledby="ba-hero-title">
      {/* Vuelta al hub sin abrir el menú. Antes vivía FUERA del hero, en una tira
          de crema propia (`BaMiga`), y por eso era la única de las cinco que se
          veía distinta a las demás — que es lo que cazó el cliente el
          25-ago-2026: «en algunas landing se muestra lo de las migas de pan, y
          en otras no». Ahora las cinco llevan la misma chapa en la misma
          esquina. */}
      <MigaGira event={event} className={styles.miga} />

      <div className={styles.scene}>
        <span className={styles.obelisco}>
          <AssetImage
            asset="buenos-aires/hero"
            className={styles.mediaFill}
            sizes="(min-width: 768px) 30vw, 45vw"
            loading="eager"
            alt={BA_HERO.obeliscoAlt}
          />
        </span>
        <span className={styles.retrato}>
          <AssetImage
            asset="azrahel/recorte"
            className={styles.mediaFill}
            sizes="(min-width: 768px) 34vw, 52vw"
            loading="eager"
            alt={BA_HERO.portraitAlt}
          />
        </span>
        <span className={styles.bandera}>
          <AssetImage
            asset="buenos-aires/bandera-seda-velo"
            className={styles.mediaFill}
            sizes="(min-width: 768px) 34vw, 100vw"
            alt=""
          />
        </span>
      </div>

      <Container width="wide">
        <div className={styles.copy}>
          {/* El mockup pone DOS piezas aqui: un medallon circular dorado
              (y 36..121, 93 px) y UNA sola linea dorada debajo. El medallon que
              dibuja el generador es una flor de la vida inventada; el emblema
              de marca de verdad es el PNG oficial (`marca/espiral`), asi que es
              el oficial quien hace de medallon — se calca la COMPOSICION, no el
              dibujo inventado. `layout="hero"` es justo eso: emblema arriba y
              "AΩGR · Escuela Pleyadiana" en una linea.
              Antes iba `layout="inline" size="sm"`, que es la forma compacta de
              la cabecera: dos lineas apiladas de ~120 px contra los 257 px de
              una sola linea del mockup.
              La flor de la vida se queda de HALO detras, que es como el mockup
              usa la geometria sagrada en el resto del hero. */}
          <Reveal delay={0.04}>
            <Link className={styles.brandLink} to="/" aria-label={BA_HERO.homeLinkLabel}>
              <span className={styles.brandHalo} aria-hidden="true">
                <SacredGeometry variant="flower" size={96} />
              </span>
              <Brandmark layout="hero" size="md" eager />
            </Link>
          </Reveal>

          {/* MEDIDO en el mockup (y 205..218): los filetes NO son una linea
              aparte encima de la ciudad, FLANQUEAN el nombre en la misma linea
              — tramo izquierdo x 306..356 y derecho x 665..712, con la ciudad
              en 379..645. O sea: filete de ~50 px, hueco de ~21 px, texto.
              Y el remate es un PUNTO REDONDO de ~4 px en el extremo INTERIOR
              (ampliado al 600 %), no el rombo de `GoldLine`; el filete se
              desvanece hacia AFUERA. Por eso aqui no vale el componente
              compartido: su rombo va al centro y no admite terminal. */}
          <Reveal delay={0.12}>
            <p className={styles.city}>
              <span className={styles.cityRule} aria-hidden="true" />
              <span className={styles.cityName}>{event.city}</span>
              <span className={styles.cityRule} aria-hidden="true" />
            </p>
          </Reveal>

          <Reveal delay={0.16}>
            <p className={styles.dates}>
              <time dateTime={event.startDate}>{formatDateRangeHero(event)}</time>
            </p>
          </Reveal>

          <Reveal delay={0.2}>
            <h1 className={styles.title} id="ba-hero-title">
              {head}{' '}
              {connector && tail ? (
                <>
                  {/* El conector se escribe UNA sola vez. Solo los dos filetes
                      son decorativos y llevan `aria-hidden`. Antes duplicaba el
                      nodo (uno oculto al lector + otro solo para el lector): se
                      anunciaba bien, pero el texto del DOM decia "de la de la",
                      y eso es una discrepancia entre contrato y salida. */}
                  <span className={styles.connector}>
                    <span className={styles.connectorRule} aria-hidden="true" />
                    {connector}
                    <span className={styles.connectorRule} aria-hidden="true" />
                  </span>{' '}
                  {tail}
                </>
              ) : null}
            </h1>
          </Reveal>

          {event.titleAccent ? (
            <Reveal delay={0.24}>
              <ScriptLine className={styles.script} size="lg" align="center">
                {event.titleAccent}
              </ScriptLine>
            </Reveal>
          ) : null}

          {/* EL VSL, ENCIMA DE LA PROMESA. Encargo del ORACLE (12-sep-2026): «debería
              ir arriba del texto rompe el motor». Y va como HERMANO del texto, no
              colgando del botón de compra: ahí dentro heredaba el ancho de un flex
              que se encoge a su contenido y el vídeo salía a 290 px en Buenos Aires
              y 478 en Bogotá —un sello de correos, y distinto en cada ciudad—.
              Aquí coge el ancho del bloque de texto, el del titular. */}
          <VslInline event={event} />

          <Reveal delay={0.28}>
            <Sparkle size={18} />
          </Reveal>

          <Reveal delay={0.32}>
            <p className={styles.promise}>
              {/* `promiseLines` ya viene partida en la config: la ultima linea va
                  en oro, como en el mockup. Cero troceo de strings aqui. */}
              {event.promiseLines.map((line, index) => (
                <span
                  className={index === event.promiseLines.length - 1 ? styles.promiseGold : undefined}
                  key={line}
                >
                  {line}{' '}
                </span>
              ))}
            </p>
          </Reveal>

          {/* «debajo de recupera tu automaestría, nos gustaría ya meter un CTA
              de compra»: el cliente señaló ESTE hueco con el ratón, y lo que
              pedía era un botón aquí dentro, no la tarjeta de la sección
              siguiente. El póster no lo dibuja — manda el cliente (ADR-006). */}
          <Reveal delay={0.36}>
            <HeroReservaCta event={event} />
          </Reveal>
        </div>
      </Container>
    </section>
  );
}
